// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: experimental voice mode on Google's Gemini Live. The microphone streams
// to Gemini as 16 kHz PCM and Gemini answers in speech (24 kHz PCM), so there is no
// Whisper or Kokoro in the loop and small talk comes back in under a second.
// Gemini hands anything about the person's own data to the Konvo Assistant through
// the ask_konvo tool; the server locked that setup into the one-use token it issued.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';

export type LiveState = 'listening' | 'hearing' | 'thinking' | 'speaking';
export type LiveError = 'mic-denied' | 'mic-missing' | 'unsupported' | 'connection';

/** One line of the session transcript kept for the experiment's logs. */
export interface LiveLogEntry {
	/** 'you' what the person said, 'gemini' what Gemini said, 'ask' a request handed to Konvo, 'konvo' its answer. */
	kind: 'you' | 'gemini' | 'ask' | 'konvo' | 'event';
	text: string;
	/** For 'konvo': how long the Assistant took. */
	ms?: number;
}

export interface LiveCallbacks {
	onState: (state: LiveState) => void;
	onLevel: (level: number) => void;
	/** What the person said this turn so far. */
	onUserText: (text: string) => void;
	/** What Gemini has said this turn so far. */
	onAssistantText: (text: string) => void;
	onError: (error: LiveError) => void;
	/** Hand a request to the Konvo Assistant and resolve with what to tell the person. */
	ask: (request: string) => Promise<string>;
	/** The Gemini voice to speak in (the server checks it). */
	voice?: string;
	/** A finished line of the conversation, for the logs. */
	onLog?: (entry: LiveLogEntry) => void;
	/** Gemini finished a turn: a good moment to send the logs. */
	onTurnDone?: () => void;
}

/** The audio side, kept apart so the protocol can be tested without a browser. */
export interface LiveAudio {
	readonly context: AudioContext | null;
	start(onChunk: (pcm: ArrayBuffer, rms: number) => void): Promise<void>;
	play(pcm: Int16Array): void;
	stopPlayback(): void;
	isPlaying(): boolean;
	/** Loudness of what is playing right now, as RMS (0..1). */
	outputRms(): number;
	close(): void;
}

interface LiveToken {
	token: string;
	model: string;
	url: string;
}

const INPUT_RATE = 16000;
const OUTPUT_RATE = 24000;
const CHUNK_SAMPLES = 640; // 40 ms at 16 kHz
const TICK_MS = 50;
const HEARING_DB = -45;
const MAX_RECONNECTS = 3;
// Gemini's voice leaks from the phone speaker back into the microphone (the phone's echo
// cancelling does not catch audio played this way). While it plays, and a moment after
// (speakers lag), the microphone only counts as the person talking over it when it is
// clearly louder than the echo expected from what is playing right now. How strongly the
// speaker reaches the microphone is learned during the session.
const ECHO_TAIL_MS = 600;
const BARGE_IN_MIN_RMS = 0.05; // about -26 dBFS, never less
const BARGE_IN_MARGIN = 3; // about 10 dB over the expected echo
const BARGE_IN_CHUNKS = 8; // 320 ms
const ECHO_WINDOW_CHUNKS = 6; // the echo lags the speaker: compare with its loudest in the last 240 ms
const START_COUPLING = 1; // until learned, assume the echo is as loud as the speaker
// Gemini cannot talk during a tool call, so while Konvo works the app plays a short
// "checking" line in the same voice, and another if it takes a while.
const STILL_WORKING_AFTER_MS = 7000;
const FILLER_RETRY_MS = 10_000;

function levelFromRms(rms: number): number {
	const db = rms > 0 ? 20 * Math.log10(rms) : -100;
	return Math.min(1, Math.max(0, (db + 50) / 35));
}

function toBase64(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let binary = '';
	for (let index = 0; index < bytes.length; index += 0x8000) {
		binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
	}
	return btoa(binary);
}

function fromBase64(data: string): Int16Array {
	const binary = atob(data);
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
	return new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
}

// Averages the microphone down to 16 kHz and posts 40 ms chunks with their loudness.
const CAPTURE_WORKLET = `
class KonvoPcmCapture extends AudioWorkletProcessor {
	constructor() {
		super();
		this.step = sampleRate / ${INPUT_RATE};
		this.position = 0; this.sum = 0; this.count = 0; this.energy = 0;
		this.out = new Int16Array(${CHUNK_SAMPLES}); this.filled = 0;
	}
	process(inputs) {
		const input = inputs[0] && inputs[0][0];
		if (!input) return true;
		for (let i = 0; i < input.length; i++) {
			this.sum += input[i]; this.count += 1; this.position += 1;
			if (this.position >= this.step) {
				this.position -= this.step;
				const value = Math.max(-1, Math.min(1, this.sum / this.count));
				this.sum = 0; this.count = 0;
				this.energy += value * value;
				this.out[this.filled++] = value < 0 ? value * 0x8000 : value * 0x7fff;
				if (this.filled === this.out.length) {
					const rms = Math.sqrt(this.energy / this.out.length);
					this.port.postMessage({pcm: this.out.buffer.slice(0), rms});
					this.filled = 0; this.energy = 0;
				}
			}
		}
		return true;
	}
}
registerProcessor('konvo-pcm-capture', KonvoPcmCapture);
`;

/** Microphone capture through an AudioWorklet and gapless playback of Gemini's PCM. */
export class BrowserLiveAudio implements LiveAudio {
	context: AudioContext | null = null;
	private stream: MediaStream | null = null;
	private output: AnalyserNode | null = null;
	private samples = new Float32Array(1024);
	private sources = new Set<AudioBufferSourceNode>();
	private playhead = 0;

	async start(onChunk: (pcm: ArrayBuffer, rms: number) => void): Promise<void> {
		if (!navigator.mediaDevices?.getUserMedia || typeof AudioWorkletNode === 'undefined')
			throw new Error('unsupported');
		const context = new AudioContext();
		this.context = context;
		void context.resume().catch(() => undefined);
		const output = context.createAnalyser();
		output.fftSize = 1024;
		output.connect(context.destination);
		this.output = output;
		this.stream = await navigator.mediaDevices.getUserMedia({
			audio: {echoCancellation: true, noiseSuppression: true, autoGainControl: true},
		});
		const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET], {type: 'application/javascript'}));
		try {
			await context.audioWorklet.addModule(url);
		} finally {
			URL.revokeObjectURL(url);
		}
		const capture = new AudioWorkletNode(context, 'konvo-pcm-capture');
		capture.port.onmessage = (event: MessageEvent<{pcm: ArrayBuffer; rms: number}>) =>
			onChunk(event.data.pcm, event.data.rms);
		context.createMediaStreamSource(this.stream).connect(capture);
		// Some browsers only run a worklet that leads somewhere; a silent gain keeps it going.
		const silent = context.createGain();
		silent.gain.value = 0;
		capture.connect(silent).connect(context.destination);
	}

	play(pcm: Int16Array): void {
		const context = this.context;
		if (!context || !this.output || pcm.length === 0) return;
		const buffer = context.createBuffer(1, pcm.length, OUTPUT_RATE);
		const channel = buffer.getChannelData(0);
		for (let index = 0; index < pcm.length; index += 1) channel[index] = pcm[index] / 0x8000;
		const source = context.createBufferSource();
		source.buffer = buffer;
		source.connect(this.output);
		this.playhead = Math.max(this.playhead, context.currentTime + 0.03);
		source.start(this.playhead);
		this.playhead += buffer.duration;
		this.sources.add(source);
		source.onended = () => this.sources.delete(source);
	}

	stopPlayback(): void {
		for (const source of this.sources) {
			source.onended = null;
			try {
				source.stop();
			} catch {
				// Already finished.
			}
		}
		this.sources.clear();
		this.playhead = 0;
	}

	isPlaying(): boolean {
		return this.sources.size > 0;
	}

	outputRms(): number {
		if (!this.output || this.sources.size === 0) return 0;
		this.output.getFloatTimeDomainData(this.samples);
		let sum = 0;
		for (const sample of this.samples) sum += sample * sample;
		return Math.sqrt(sum / this.samples.length);
	}

	close(): void {
		this.stopPlayback();
		for (const track of this.stream?.getTracks() ?? []) track.stop();
		this.stream = null;
		// The session closes the context after its closing chime.
	}
}

interface LiveMessage {
	setupComplete?: unknown;
	goAway?: unknown;
	toolCall?: {functionCalls?: Array<{id?: string; name?: string; args?: {request?: unknown}}>};
	toolCallCancellation?: {ids?: Array<string>};
	serverContent?: {
		modelTurn?: {parts?: Array<{inlineData?: {data?: string}}>};
		inputTranscription?: {text?: string};
		outputTranscription?: {text?: string};
		interrupted?: boolean;
		turnComplete?: boolean;
	};
}

export class GeminiLiveEngine {
	private socket: WebSocket | null = null;
	private muted = false;
	private closed = false;
	private pendingTools = 0;
	private micRms = 0;
	private state: LiveState = 'listening';
	private userText = '';
	private assistantText = '';
	private turnDone = true;
	private userLogged = false;
	private assistantLogged = 0;
	private lastPlayback = 0;
	private loudChunks = 0;
	private heldChunks: Array<ArrayBuffer> = [];
	private bargedIn = false;
	private outputHistory: Array<number> = [];
	/** Microphone level per unit of speaker level while Gemini plays (learned). */
	private coupling = START_COUPLING;
	private couplingSamples = 0;
	private peakMic = 0;
	private peakOutput = 0;
	/** The request Konvo is working on, and every tool call waiting for its answer. */
	private pendingAsk: {calls: Array<{id?: string; name?: string}>} | null = null;
	private cancelledCalls = new Set<string>();
	/** After talking over Gemini, the rest of its interrupted answer is not played. */
	private dropModelAudio = false;
	private fillers: {checking: Array<Int16Array>; still: Array<Int16Array>} = {checking: [], still: []};
	private ticker: ReturnType<typeof setInterval> | null = null;
	private reconnects = 0;

	constructor(
		private readonly callbacks: LiveCallbacks,
		readonly audio: LiveAudio = new BrowserLiveAudio(),
		private readonly openSocket: (url: string) => WebSocket = (url) => new WebSocket(url),
	) {}

	/**
	 * Resolves once the session is live. Rejects, for the caller to fall back to the
	 * Konvo voices, when Gemini cannot be reached or the browser lacks what it needs.
	 * The microphone and audio start first, inside the tap that opened voice mode (phones
	 * insist), while the token and connection are fetched alongside.
	 */
	async start(): Promise<void> {
		const audio = this.audio
			.start((pcm, rms) => this.sendAudio(pcm, rms))
			.then(
				() => null,
				(error: unknown) => error,
			);
		let connection: unknown = null;
		try {
			await this.connect(await this.fetchToken());
		} catch (error) {
			connection = error ?? new Error('connection');
		}
		const micError = await audio;
		const name = micError instanceof DOMException ? micError.name : micError instanceof Error ? micError.message : '';
		if (connection || name === 'unsupported') {
			this.close();
			throw connection ?? micError;
		}
		if (micError) {
			this.close();
			this.callbacks.onError(
				name === 'NotFoundError' || name === 'OverconstrainedError' ? 'mic-missing' : 'mic-denied',
			);
			return;
		}
		this.ticker = setInterval(() => this.tick(), TICK_MS);
		void this.loadFillers(true);
	}

	/** The "checking" lines in this voice; the first session in a new voice may have to wait for them. */
	private async loadFillers(retry: boolean): Promise<void> {
		try {
			const response = await http.post<{clips?: Array<{kind?: string; pcm?: string; rate?: number}>}>(
				Endpoints.KONVO_ASSISTANT_VOICE_GEMINI_FILLERS,
				{body: this.callbacks.voice ? {voice: this.callbacks.voice} : {}},
			);
			const fillers: typeof this.fillers = {checking: [], still: []};
			for (const clip of response.body?.clips ?? []) {
				if (!clip.pcm || (clip.rate ?? OUTPUT_RATE) !== OUTPUT_RATE) continue;
				if (clip.kind === 'checking' || clip.kind === 'still') fillers[clip.kind].push(fromBase64(clip.pcm));
			}
			this.fillers = fillers;
			if (retry && fillers.checking.length === 0 && !this.closed) {
				setTimeout(() => void (this.closed ? undefined : this.loadFillers(false)), FILLER_RETRY_MS);
			}
		} catch {
			// Without them Konvo's answer still comes; there is just a quiet wait.
		}
	}

	private playFiller(kind: 'checking' | 'still'): void {
		const clips = this.fillers[kind];
		if (clips.length === 0 || this.audio.isPlaying()) return;
		this.audio.play(clips[Math.floor(Math.random() * clips.length)]!);
	}

	setMuted(muted: boolean): void {
		this.muted = muted;
		if (muted) this.send({realtimeInput: {audioStreamEnd: true}});
	}

	/** Tap on the ribbon while Gemini talks: stop it speaking (talking over it does the same). */
	interrupt(): void {
		this.audio.stopPlayback();
	}

	close(): void {
		this.closed = true;
		if (this.ticker) clearInterval(this.ticker);
		this.ticker = null;
		const socket = this.socket;
		this.socket = null;
		if (socket && socket.readyState <= 1) socket.close(1000);
		this.audio.close();
	}

	private async fetchToken(): Promise<LiveToken> {
		const response = await http.post<LiveToken>(Endpoints.KONVO_ASSISTANT_VOICE_LIVE_TOKEN, {
			body: this.callbacks.voice ? {voice: this.callbacks.voice} : {},
		});
		const token = response.body;
		if (!token?.token || !token.url) throw new Error('No token');
		return token;
	}

	private connect(token: LiveToken): Promise<void> {
		return new Promise((resolve, reject) => {
			const socket = this.openSocket(`${token.url}?access_token=${encodeURIComponent(token.token)}`);
			this.socket = socket;
			let ready = false;
			socket.onopen = () => socket.send(JSON.stringify({setup: {model: `models/${token.model}`}}));
			socket.onmessage = (event) => {
				void this.readMessage(event.data).then((message) => {
					if (!message) return;
					if (message.setupComplete && !ready) {
						ready = true;
						resolve();
						return;
					}
					this.handle(message);
				});
			};
			socket.onerror = () => {
				if (!ready) reject(new Error('connection'));
			};
			socket.onclose = () => {
				if (!ready) reject(new Error('connection'));
				else if (this.socket === socket) void this.reconnect();
			};
		});
	}

	/** Google ends a connection after about ten minutes (goAway first); carry on in a new one. */
	private async reconnect(): Promise<void> {
		if (this.closed) return;
		this.socket = null;
		if (this.reconnects >= MAX_RECONNECTS) {
			this.callbacks.onError('connection');
			return;
		}
		this.reconnects += 1;
		try {
			await this.connect(await this.fetchToken());
			this.log('event', 'reconnected');
		} catch {
			if (this.closed) return;
			this.log('event', 'connection lost');
			this.callbacks.onError('connection');
		}
	}

	private async readMessage(data: unknown): Promise<LiveMessage | null> {
		try {
			const text = typeof data === 'string' ? data : data instanceof Blob ? await data.text() : String(data);
			return JSON.parse(text) as LiveMessage;
		} catch {
			return null;
		}
	}

	private send(message: unknown): void {
		if (this.socket?.readyState === 1) this.socket.send(JSON.stringify(message));
	}

	private sendAudio(pcm: ArrayBuffer, rms: number): void {
		this.micRms = this.muted ? 0 : rms;
		if (this.muted) return;
		const now = Date.now();
		if (this.audio.isPlaying()) this.lastPlayback = now;
		this.outputHistory.push(this.audio.outputRms());
		if (this.outputHistory.length > ECHO_WINDOW_CHUNKS) this.outputHistory.shift();
		if (now - this.lastPlayback >= ECHO_TAIL_MS) {
			if (this.peakMic > 0) this.logEchoStretch();
			this.bargedIn = false;
			this.loudChunks = 0;
			this.heldChunks = [];
			this.sendChunk(pcm);
			return;
		}
		if (this.bargedIn) {
			this.sendChunk(pcm);
			return;
		}
		const speaker = Math.max(...this.outputHistory);
		const expectedEcho = this.coupling * speaker;
		const threshold = Math.max(BARGE_IN_MIN_RMS, expectedEcho * BARGE_IN_MARGIN);
		this.peakMic = Math.max(this.peakMic, rms);
		this.peakOutput = Math.max(this.peakOutput, speaker);
		// Hold the last moment of sound, so a real interruption keeps its first words.
		this.heldChunks.push(pcm);
		if (this.heldChunks.length > BARGE_IN_CHUNKS) this.heldChunks.shift();
		if (rms < threshold) {
			this.loudChunks = 0;
			this.learnCoupling(rms, speaker);
			return;
		}
		this.loudChunks += 1;
		if (this.loudChunks < BARGE_IN_CHUNKS) return;
		this.bargedIn = true;
		this.dropModelAudio = true;
		this.audio.stopPlayback();
		this.log(
			'event',
			`talked over Gemini (mic ${rms.toFixed(3)}, expected echo ${expectedEcho.toFixed(3)}, coupling ${this.coupling.toFixed(2)})`,
		);
		for (const chunk of this.heldChunks) this.sendChunk(chunk);
		this.heldChunks = [];
	}

	/** Quiet chunks while Gemini plays are its echo: they teach how loud the echo gets. */
	private learnCoupling(rms: number, speaker: number): void {
		if (speaker < 0.005) return;
		const ratio = Math.min(3, rms / speaker);
		this.couplingSamples += 1;
		// The first few set it quickly; after that it follows slowly.
		const weight = this.couplingSamples <= 5 ? 1 / this.couplingSamples : 0.1;
		this.coupling += (ratio - this.coupling) * weight;
	}

	/** One line per stretch of Gemini speaking, so the thresholds can be checked against real phones. */
	private logEchoStretch(): void {
		this.log(
			'event',
			`echo: loudest mic ${this.peakMic.toFixed(3)} while speaker ${this.peakOutput.toFixed(3)}, coupling ${this.coupling.toFixed(2)}`,
		);
		this.peakMic = 0;
		this.peakOutput = 0;
	}

	private sendChunk(pcm: ArrayBuffer): void {
		this.send({realtimeInput: {audio: {data: toBase64(pcm), mimeType: `audio/pcm;rate=${INPUT_RATE}`}}});
	}

	private handle(message: LiveMessage): void {
		if (message.goAway) {
			const old = this.socket;
			this.socket = null;
			old?.close(1000);
			void this.reconnect();
			return;
		}
		for (const id of message.toolCallCancellation?.ids ?? []) {
			this.cancelledCalls.add(id);
			this.log('event', 'Gemini cancelled a request');
		}
		for (const call of message.toolCall?.functionCalls ?? []) void this.runTool(call);
		const content = message.serverContent;
		if (!content) return;
		const input = content.inputTranscription?.text;
		if (input) {
			// A new question starts fresh captions.
			if (this.turnDone) {
				this.userText = '';
				this.assistantText = '';
				this.userLogged = false;
				this.assistantLogged = 0;
				this.callbacks.onAssistantText('');
				this.turnDone = false;
			}
			this.userText += input;
			this.callbacks.onUserText(this.userText.trim());
		}
		for (const part of content.modelTurn?.parts ?? []) {
			if (!part.inlineData?.data || this.dropModelAudio) continue;
			// Gemini is answering again: the person has had their say, so its voice is echo once more.
			if (this.bargedIn) {
				this.bargedIn = false;
				this.loudChunks = 0;
				this.heldChunks = [];
			}
			this.audio.play(fromBase64(part.inlineData.data));
		}
		const output = content.outputTranscription?.text;
		if (output) {
			this.assistantText += output;
			this.callbacks.onAssistantText(this.assistantText.replace(/\s+/g, ' ').trim());
		}
		if (content.interrupted) {
			this.audio.stopPlayback();
			this.dropModelAudio = false;
			this.log('event', 'interrupted');
		}
		if (content.turnComplete) {
			this.turnDone = true;
			this.dropModelAudio = false;
			this.logTurn();
			this.callbacks.onTurnDone?.();
		}
	}

	private log(kind: LiveLogEntry['kind'], text: string, ms?: number): void {
		const clean = text.replace(/\s+/g, ' ').trim();
		if (clean) this.callbacks.onLog?.(ms === undefined ? {kind, text: clean} : {kind, text: clean, ms});
	}

	/** What was said since the last log: the question once, then each new stretch of Gemini's answer. */
	private logTurn(): void {
		if (!this.userLogged && this.userText.trim()) {
			this.userLogged = true;
			this.log('you', this.userText);
		}
		const said = this.assistantText.slice(this.assistantLogged);
		this.assistantLogged = this.assistantText.length;
		this.log('gemini', said);
	}

	private async runTool(call: {id?: string; name?: string; args?: {request?: unknown}}): Promise<void> {
		const request = typeof call.args?.request === 'string' ? call.args.request.trim() : '';
		if (!request) {
			this.send({
				toolResponse: {
					functionResponses: [{id: call.id, name: call.name, response: {result: 'Ask them what they need.'}}],
				},
			});
			return;
		}
		// Konvo is already on it (Gemini asked again, often after an interruption): this call
		// waits for the same answer instead of starting a second search.
		if (this.pendingAsk) {
			this.pendingAsk.calls.push(call);
			this.log('event', `asked again while Konvo works: ${request}`);
			return;
		}
		const pending = {calls: [call]};
		this.pendingAsk = pending;
		this.pendingTools += 1;
		// What was heard before the hand-off belongs before it in the log.
		this.logTurn();
		this.log('ask', request);
		const askedAt = Date.now();
		// Gemini waits for the answer in silence; fill it in its own voice.
		this.playFiller('checking');
		const still = setTimeout(() => this.playFiller('still'), STILL_WORKING_AFTER_MS);
		let answer: string;
		try {
			answer = await this.callbacks.ask(request);
		} catch {
			answer = "Konvo couldn't be reached just now. Tell them, and suggest trying again.";
		} finally {
			clearTimeout(still);
			this.pendingTools -= 1;
			this.pendingAsk = null;
		}
		this.log('konvo', answer, Date.now() - askedAt);
		if (this.closed) return;
		const open = pending.calls.filter((waiting) => !waiting.id || !this.cancelledCalls.has(waiting.id));
		if (open.length === 0) {
			this.log('event', 'answer not spoken: Gemini cancelled the request');
			return;
		}
		// The newest call carries the answer; any earlier one still open is told it follows.
		this.send({
			toolResponse: {
				functionResponses: open.map((waiting, index) => ({
					id: waiting.id,
					name: waiting.name,
					response: {result: index === open.length - 1 ? answer : 'Answered in the next result.'},
				})),
			},
		});
	}

	private tick(): void {
		const playing = this.audio.isPlaying();
		const next: LiveState =
			this.pendingTools > 0
				? 'thinking'
				: playing
					? 'speaking'
					: this.micRms > 0 && 20 * Math.log10(this.micRms) > HEARING_DB
						? 'hearing'
						: 'listening';
		if (next !== this.state) {
			this.state = next;
			this.callbacks.onState(next);
		}
		this.callbacks.onLevel(levelFromRms(playing ? this.audio.outputRms() : this.micRms));
	}
}
